import { useEffect } from 'react';
import { useBlocker } from 'react-router-dom';

/**
 * Hook that warns users when navigating away with unsaved changes.
 * Handles both in-app navigation (React Router useBlocker) and
 * browser navigation (beforeunload for tab close/refresh).
 *
 * @param {boolean} isDirty - Whether the form has unsaved changes
 * @returns {object} blocker - React Router blocker object
 *   blocker.state: 'unblocked' | 'blocked' | 'proceeding'
 *   blocker.proceed(): allow the blocked navigation
 *   blocker.reset(): cancel the blocked navigation
 */
export default function useUnsavedChanges(isDirty) {
  // Block in-app navigation via React Router
  const blocker = useBlocker(
    ({ currentLocation, nextLocation }) =>
      isDirty && currentLocation.pathname !== nextLocation.pathname
  );

  // Block browser close / refresh / external navigation
  useEffect(() => {
    if (!isDirty) return;
    const handler = (e) => {
      e.preventDefault();
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [isDirty]);

  return blocker;
}
